import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  afterNextRender,
  inject,
  signal,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { InteractionRecord } from '../../core/models/interaction.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CalculatedFieldEvaluatorService } from '../../core/services/calculated-field-evaluator.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FeedbackSoundService } from '../../core/services/feedback-sound.service';
import { FormDefinitionService } from '../../core/services/form-definition.service';
import { FormService } from '../../core/services/form.service';
import { FormSumbissionService } from '../../core/services/formSubmission.service';
import { LoggerService } from '../../core/services/logger.service';
import { TemplateMetadataService } from '../../core/services/template-metadata.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserService } from '../../core/services/user.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { CheckpointFormHeaderComponent } from '../../shared/components/checkpoint-form-header/checkpoint-form-header.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { applyVisibilityConditions } from '../../shared/utils/field-visibility.utils';

/**
 * Component for recording a new interaction with an asset
 * Displays a dynamic form based on the interaction type's form definition
 */
@Component({
  selector: 'sumbit-form',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    TranslatePipe,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CheckpointFormHeaderComponent,
    SpinnerComponent,
    ButtonComponent,
    IconComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './submit-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./submit-form.component.css'],
})
export class SubmitFormComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private formService = inject(FormService);
  private userService = inject(UserService);
  private interactionService = inject(FormSumbissionService);
  private formDefinitionService = inject(FormDefinitionService);
  private calculatedEvaluator = inject(CalculatedFieldEvaluatorService);
  private templateMetadataService = inject(TemplateMetadataService);
  private feedbackSound = inject(FeedbackSoundService);
  private injector = inject(Injector);

  formlyGroup = new FormGroup({});
  formlyOptions: FormlyFormOptions = {};
  model: any = {};
  fields: FormlyFieldConfig[] = [];

  loading = signal(true);
  saving = signal(false);
  error = signal<string | null>(null);

  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  instance = signal<AssetInstance | null>(null);
  /** Column count for the rendered form grid (1 = single column, default). */
  formColumns = signal<1 | 2 | 3 | 4>(1);

  private logger = inject(LoggerService).getLogger('formSubmissionComponent');
  private translate = inject(TranslationService);
  private breadcrumbService = inject(BreadcrumbService);

  // Return URL for navigation after completion (cancel only)
  private returnUrl: string | null = null;
  private lastInteractionFormData: Record<string, any> | null = null;
  private lastInteractionRecord: InteractionRecord | null = null;

  /**
   * Tracks the last value each state-transition output field was automatically
   * assigned. Lets us re-sync the suggested value when inputs change while still
   * respecting a manual override the user makes to repair the state.
   */
  private stateTransitionAssignedOutputs = new Map<string, string | number | null>();

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.loading.set(true);
    this.error.set(null);

    try {
      // Get checkpoint and interaction type from route params
      const checkPointId = this.route.snapshot.paramMap.get('checkpointId');
      const formId = this.route.snapshot.paramMap.get('formId');
      const instanceId = this.route.snapshot.paramMap.get('instanceId');

      if (!checkPointId || !formId) {
        this.error.set(this.translate.translate('submission.form.error.missingParams'));
        this.loading.set(false);
        return;
      }

      // Load asset
      const checkPoint = await this.checkPointService.getById(checkPointId);
      if (!checkPoint) {
        this.error.set(
          this.translate.translate('submission.form.error.checkpointNotFound', {
            checkpointId: checkPointId,
          }),
        );
        this.loading.set(false);
        return;
      }
      this.checkPoint.set(checkPoint);

      // Load interaction type
      const form = await this.formService.getById(formId);
      if (!form) {
        this.error.set(
          this.translate.translate('submission.form.error.formNotFound', {
            formId: formId,
          }),
        );
        this.loading.set(false);
        return;
      }
      this.form.set(form);

      // Load instance if specified
      if (instanceId) {
        const instance = await this.assetInstanceService.getById(instanceId);
        if (instance) {
          this.instance.set(instance);
        }
      }

      this.formlyOptions.formState = {
        templateContext: this.templateMetadataService.buildContext(checkPoint, this.instance()),
      };

      // Load form definition
      if (form.form_definition_id) {
        const formDef = await this.formDefinitionService.getFormDefinitionFull(
          form.form_definition_id,
        );
        if (formDef) {
          this.logger.debug('Raw form definition from DB:', formDef);
          this.logger.debug('Form definition structure:', JSON.stringify(formDef, null, 2));
          this.fields = applyVisibilityConditions(formDef.fields);
          this.formColumns.set(formDef.columns);
          this.logger.debug('Fields assigned. Fields array:', this.fields);
        } else {
          this.error.set(this.translate.translate('submission.form.error.formDefNotFound'));
          this.loading.set(false);
          return;
        }
      } else {
        // No custom form - just record the interaction with timestamp auto-captured by DB
        // Empty form means user just confirms the interaction happened
        this.fields = [];
      }

      // Pre-fill with last interaction values if enabled at form or field level
      if (form.form_definition_id) {
        const formDefault = form.prefill_last_values ?? false;
        const anyFieldPrefills =
          formDefault || this.fields.some((f) => f.props?.['prefillLastValue'] === 'always');
        if (anyFieldPrefills) {
          await this.prefillFromLastInteraction(checkPointId, formId, formDefault);
        }
      }

      // Capture returnUrl parameter for cancel navigation
      this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

      const instanceForBreadcrumb = this.instance();
      const breadcrumbLabels = instanceForBreadcrumb
        ? [checkPoint.name, `Submit ${form.name}`, `#${instanceForBreadcrumb.serial_number}`]
        : [checkPoint.name, `Submit ${form.name}`];
      this.breadcrumbService.setDynamicLabels(breadcrumbLabels);

      this.loading.set(false);

      if (form.auto_submit) {
        this.scheduleAutoSubmit();
      }
    } catch (err) {
      this.logger.error('Failed to load data:', err);
      this.error.set(this.translate.translate('submission.form.error.loadFailed'));
      this.loading.set(false);
    }
  }

  /**
   * For `auto_submit` forms: save straight away when nothing needs filling in
   * (e.g. a patrol "I was here" form). A form with required fields left empty
   * stays open for the user to complete.
   *
   * Formly only creates the form controls when <formly-form> renders, and an
   * empty FormGroup reports VALID, so validity means nothing until the
   * controls exist. Wait (up to a few renders) for them before deciding.
   */
  private scheduleAutoSubmit(rendersLeft = SubmitFormComponent.AUTO_SUBMIT_MAX_RENDERS): void {
    afterNextRender(
      () => {
        if (!this.formControlsReady()) {
          if (rendersLeft > 1) {
            this.scheduleAutoSubmit(rendersLeft - 1);
          } else {
            this.logger.warn('Auto-submit skipped: form controls never rendered');
          }
          return;
        }
        void this.autoSubmitIfValid();
      },
      { injector: this.injector },
    );
  }

  private static readonly AUTO_SUBMIT_MAX_RENDERS = 5;

  /** True once Formly has created a control for every keyed field (trivially for no fields). */
  private formControlsReady(): boolean {
    const keyed = this.fields.filter((f) => f.key !== undefined && f.key !== null).length;
    return keyed === 0 || Object.keys(this.formlyGroup.controls).length > 0;
  }

  async autoSubmitIfValid(): Promise<void> {
    if (this.saving() || !this.formControlsReady() || this.formlyGroup.invalid) {
      this.logger.debug('Auto-submit skipped:', {
        saving: this.saving(),
        ready: this.formControlsReady(),
        invalid: this.formlyGroup.invalid,
      });
      return;
    }
    this.logger.debug('Auto-submitting form');
    await this.onSubmit();
  }

  async onSubmit() {
    if (this.formlyGroup.invalid) {
      this.formlyGroup.markAllAsTouched();
      return;
    }

    const checkPoint = this.checkPoint();
    const form = this.form();

    if (!checkPoint || !form) {
      this.error.set(this.translate.translate('submission.form.error.missingData'));
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    try {
      // Add current user's name to form data automatically
      const currentUser = this.userService.currentUser();

      const mergedModelWithCalculated = await this.recomputeCalculatedValues();

      // DEBUG: Log the form group and model values
      this.logger.debug('=== FORM SUBMISSION DEBUG ===');
      this.logger.debug('Form group value:', this.formlyGroup.value);
      this.logger.debug('Form group controls:', Object.keys(this.formlyGroup.controls));
      this.logger.debug('Form group controls detail:', this.formlyGroup.controls);
      this.logger.debug('Model:', this.model);
      this.logger.debug('Fields count:', this.fields.length);
      this.logger.debug('Fields:', this.fields);
      this.logger.debug('Fields structure:', JSON.stringify(this.fields, null, 2));

      const formDataWithUser = {
        ...mergedModelWithCalculated,
        _recorded_by: currentUser?.full_name || 'Unknown User',
      };

      this.logger.debug('Form data with user:', formDataWithUser);

      // Save the interaction record to local SQLite
      this.logger.debug('>>> About to call interactionService.create()', {
        checkPoint_id: checkPoint.id,
        form_id: form.id,
      });
      const saved = await this.interactionService.create({
        checkPoint_id: checkPoint.id!,
        form_id: form.id!,
        asset_instance_id: this.instance()?.id,
        form_data: formDataWithUser,
      });
      this.logger.debug('>>> interactionService.create() returned:', saved);

      if (form.play_success_sound) {
        void this.feedbackSound.playSuccess();
      }

      // Navigate to success screen; replaceUrl so browser-back skips the form
      const instanceForNav = this.instance();
      const donePath = instanceForNav
        ? ['/submission', checkPoint.id, form.id, instanceForNav.id, 'done']
        : ['/submission', checkPoint.id, form.id, 'done'];
      this.router.navigate(donePath, {
        replaceUrl: true,
      });
    } catch (err) {
      this.logger.error('Failed to save interaction:', err);
      this.error.set(this.translate.translate('submission.form.error.saveFailed'));
      this.saving.set(false);
    }
  }

  async onModelChange(updatedModel: any): Promise<void> {
    this.model = updatedModel;
    await this.recomputeCalculatedValues();
  }

  /**
   * Shows a validation summary banner near the submit buttons once the user has
   * directly interacted with (and left) a field that is still invalid.
   *
   * Returns null when:
   * - The form is valid (banner not needed)
   * - No invalid field has been touched yet (don't scold before user has tried)
   *
   * Per-field inline errors (shown below each field on blur) handle the specific
   * field-level feedback. This banner tells users near the submit button that
   * something above them still needs attention — especially important on long
   * mobile forms where errored fields may be scrolled out of view.
   */
  get validationMessage(): string | null {
    if (this.formlyGroup.valid) return null;
    return this.hasInvalidTouchedControl(this.formlyGroup)
      ? this.translate.translate('submission.form.validation.requiredFields')
      : null;
  }

  /**
   * Recursively returns true if any control in the group is both invalid and
   * touched or dirty — i.e. the user has directly interacted with it.
   */
  private hasInvalidTouchedControl(control: AbstractControl): boolean {
    if (control instanceof FormGroup) {
      return Object.values(control.controls).some((child) => this.hasInvalidTouchedControl(child));
    }
    if (control instanceof FormArray) {
      return control.controls.some((child) => this.hasInvalidTouchedControl(child));
    }
    return control.invalid && (control.touched || control.dirty);
  }

  cancel() {
    // Navigate to returnUrl if provided, otherwise go to choose-form screen
    if (this.returnUrl?.startsWith('/')) {
      void this.router.navigateByUrl(this.returnUrl);
      return;
    }

    void this.router.navigate(['/submission', this.checkPoint()?.id]);
  }

  /**
   * Pre-fill the form with values from the last interaction.
   *
   * Per-field behaviour:
   *   - field.props.prefillLastValue === true  → always pre-fill this field
   *   - field.props.prefillLastValue === false → never pre-fill this field
   *   - field.props.prefillLastValue undefined  → use formPrefillDefault
   *
   * System fields (prefixed with _ or in the exclusion list) are always skipped.
   */
  private async prefillFromLastInteraction(
    checkpointId: string,
    interactionTypeId: string,
    formPrefillDefault: boolean,
  ): Promise<void> {
    try {
      const instanceId = this.instance()?.id;
      const lastInteraction = instanceId
        ? await this.interactionService.getLastByCheckPointAndTypeAndInstance(
            checkpointId,
            interactionTypeId,
            instanceId,
          )
        : await this.interactionService.getLastByCheckPointAndType(checkpointId, interactionTypeId);

      if (!lastInteraction || !lastInteraction.form_data) {
        return; // No previous interaction to pre-fill from
      }

      // Filter out system/computed fields that shouldn't be pre-filled
      const systemFields = ['_recorded_by', 'created_at', 'updated_at', 'id'];
      const prefillData: Record<string, any> = {};

      for (const [key, value] of Object.entries(lastInteraction.form_data)) {
        if (systemFields.includes(key) || key.startsWith('_')) continue;

        // Look up the per-field override, falling back to the form-level default
        const fieldConfig = this.fields.find((f) => f.key === key);
        if (fieldConfig?.type === 'calculated') {
          continue;
        }
        const fieldOverride = fieldConfig?.props?.['prefillLastValue'];
        // 'always' → pre-fill; 'never' → skip; 'default'/undefined → use form default
        const shouldPrefill =
          fieldOverride === 'always' || (fieldOverride !== 'never' && formPrefillDefault);

        if (shouldPrefill) {
          prefillData[key] = value;
        }
      }

      // Set the model with pre-filled values
      this.model = prefillData;
      this.lastInteractionRecord = lastInteraction;
      this.lastInteractionFormData = lastInteraction.form_data;

      await this.recomputeCalculatedValues();

      this.logger.debug('Pre-filled form with last interaction values:', prefillData);
    } catch (error) {
      this.logger.error('Failed to pre-fill from last interaction:', error);
      // Don't show error to user - just skip pre-filling
    }
  }

  private async recomputeCalculatedValues(): Promise<Record<string, any>> {
    const calculatedFields = this.fields.filter((f) => f.type === 'calculated');
    if (calculatedFields.length === 0) {
      return this.model;
    }

    const previousSubmissionData =
      await this.getPreviousSubmissionFormDataIfNeeded(calculatedFields);
    const nowIsoString = new Date().toISOString();
    const calculatedValues = this.calculatedEvaluator.evaluateCalculatedFields(
      this.model,
      this.fields,
      previousSubmissionData?.formData,
      {
        nowIsoString,
        currentSubmissionCreatedAt: nowIsoString,
        previousSubmissionCreatedAt: previousSubmissionData?.createdAt,
      },
    );
    const stateTransitionOutputValues = this.getStateTransitionOutputAssignments(
      calculatedFields,
      calculatedValues,
    );

    const nextModel = {
      ...this.model,
      ...calculatedValues,
      ...stateTransitionOutputValues,
    };

    this.model = nextModel;
    return nextModel;
  }

  private getStateTransitionOutputAssignments(
    calculatedFields: FormlyFieldConfig[],
    calculatedValues: Record<string, string | number | null>,
  ): Record<string, string | number | null> {
    const assignments: Record<string, string | number | null> = {};

    for (const field of calculatedFields) {
      if (field.props?.['calculationKind'] !== 'state_transition') {
        continue;
      }

      // If no explicit outputFieldId is configured, default to sourceFieldId so
      // the computed next-state is written back to the source field — enabling
      // automatic state-machine cycling without extra configuration.
      const configuredOutputFieldId = String(field.props?.['outputFieldId'] ?? '').trim();
      const sourceFieldId = String(field.props?.['sourceFieldId'] ?? '').trim();
      const outputFieldId = configuredOutputFieldId || sourceFieldId;
      const calculatedFieldKey = typeof field.key === 'string' ? field.key : '';
      if (!outputFieldId || !calculatedFieldKey) {
        continue;
      }

      if (!(calculatedFieldKey in calculatedValues)) {
        continue;
      }

      const computedValue = calculatedValues[calculatedFieldKey] ?? null;

      // Don't overwrite an existing value with null — if there's no applicable
      // transition (e.g. first submission with no previous state), leave the
      // source field as-is rather than clearing the user's selection.
      if (computedValue === null) {
        continue;
      }
      const currentValue =
        (this.model[outputFieldId] as string | number | null | undefined) ?? null;
      const lastAssignedValue = this.stateTransitionAssignedOutputs.get(outputFieldId);

      // If we previously wrote a suggested value and the user has since changed
      // the output field away from it, respect that manual override (e.g. fixing
      // a wrong state). The user can clear the override by re-selecting the
      // suggested value. While not overridden, the output stays in sync with the
      // recomputed value so it updates when relevant inputs change.
      const userOverrodeSuggestion =
        this.stateTransitionAssignedOutputs.has(outputFieldId) &&
        !Object.is(currentValue, lastAssignedValue);

      if (userOverrodeSuggestion) {
        continue;
      }

      assignments[outputFieldId] = computedValue;
      this.stateTransitionAssignedOutputs.set(outputFieldId, computedValue);
    }

    return assignments;
  }

  private async getPreviousSubmissionFormDataIfNeeded(
    calculatedFields: FormlyFieldConfig[],
  ): Promise<{ formData: Record<string, any>; createdAt?: string } | null> {
    if (!this.needsPreviousSubmissionData(calculatedFields)) {
      return null;
    }

    if (this.lastInteractionFormData || this.lastInteractionRecord) {
      return {
        formData: this.lastInteractionFormData ?? {},
        createdAt: this.lastInteractionRecord?.created_at,
      };
    }

    const checkPointId = this.checkPoint()?.id;
    const formId = this.form()?.id;
    if (!checkPointId || !formId) {
      return null;
    }

    try {
      const instanceId = this.instance()?.id;
      const lastInteraction = instanceId
        ? await this.interactionService.getLastByCheckPointAndTypeAndInstance(
            checkPointId,
            formId,
            instanceId,
          )
        : await this.interactionService.getLastByCheckPointAndType(checkPointId, formId);
      this.lastInteractionRecord = lastInteraction;
      this.lastInteractionFormData = lastInteraction?.form_data ?? null;
      return {
        formData: this.lastInteractionFormData ?? {},
        createdAt: lastInteraction?.created_at,
      };
    } catch (error) {
      this.logger.error('Failed to fetch previous submission for calculated fields:', error);
      return null;
    }
  }

  private needsPreviousSubmissionData(calculatedFields: FormlyFieldConfig[]): boolean {
    return calculatedFields.some(
      (field) =>
        field.props?.['calculationKind'] === 'date_diff_from_previous' ||
        field.props?.['calculationKind'] === 'state_transition',
    );
  }
}
